import { UserAuthorizationLevel } from "Store/advice/interfaces";
import {
  CollectiveAdvice,
  CoverageNeed,
  NeedName,
  PaymentConfiguration,
  ProductCoverageName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";

import {
  getPayment,
  getPaymentRestriction,
  getRecommended,
  getSelectedCoverageValue,
  isAllowedAmount,
  isDisplayedAndChecked,
} from "Store/akaAdvice/interfaces/getters";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { DisabilityProduct } from "Store/interfaces/products/checkout/lifeProducts/DisabilityProduct";
import { isDefined, isNotEmpty } from "Utils/functions";
import { OneTimePaymentAdvisorChoice } from "../ProductPrice/AdviceCards/interfaces";
import { DOCUMENT_MAX_COMMENT_LINES } from "../adviceConstants";
import { CONTACT_ADVISOR_MIN_CAPITAL_DIFF } from "./AkaAdviceCards/DisabilityAdviceCard/Forms/disabilityConstants";
import { AkaAdvicePriceFormValues } from "./AkaAdviceCards/interfaces";

interface WeightedNeed {
  readonly weight: number;
}

const defaultWeight: Partial<Record<NeedName, number>> = {
  [NeedName.LIFE]: 0.51,
  [NeedName.DISABILITY]: 0.5,
  [NeedName.CRITICAL_ILLNESS]: 0.49,
};

function getWeight(product: NeedName, weight: number | undefined): number {
  const value = defaultWeight[product];
  const startWeight = value ?? 0.5;
  const addedWeight = weight ?? 0;
  return startWeight + addedWeight;
}

export interface SortedNeed {
  readonly name: NeedName;
  readonly coverageNeed: CoverageNeed;
}

export function sortNeeds<T extends SortedNeed>(
  needs: Array<T>,
  weights: Partial<Record<NeedName, number>>,
): Array<T> {
  const compareWeightedNeed = (
    needA: WeightedNeed,
    needB: WeightedNeed,
  ): number => needB.weight - needA.weight;

  return needs
    .map((need) => ({
      ...need,
      weight: getWeight(need.name, weights[need.name]),
    }))
    .sort(compareWeightedNeed);
}

export const entranceToNeed = (
  entrance: INSURANCE_NAME_VALUE | null,
): NeedName | undefined => {
  switch (entrance) {
    case INSURANCE_NAME_VALUE.DEATH:
      return NeedName.LIFE;
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
      return NeedName.DISABILITY;
    case INSURANCE_NAME_VALUE.CRITICAL_ILLNESS:
      return NeedName.CRITICAL_ILLNESS;
    default:
      return undefined;
  }
};

export const collectiveAdvicesChecked = (
  collectiveAdvice: CollectiveAdvice,
): boolean[] =>
  [
    collectiveLifeAdviceChecked(collectiveAdvice.needs.LIFE),
    collectiveDisabilityAdviceChecked(collectiveAdvice.needs.DISABILITY),
    collectiveCriticalIllnessAdviceChecked(
      collectiveAdvice.needs.CRITICAL_ILLNESS,
    ),
  ].filter((allowed) => allowed);

export const individualAdvicesChecked = (
  collectiveAdvice: CollectiveAdvice,
): boolean[] =>
  [
    individualLifeAdviceChecked(collectiveAdvice.needs.LIFE),
    individualDisabilityAdviceChecked(collectiveAdvice.needs.DISABILITY),
    individualCriticalIllnessAdviceChecked(
      collectiveAdvice.needs.CRITICAL_ILLNESS,
    ),
  ].filter((allowed) => allowed);

const collectiveLifeAdviceChecked = (need: CoverageNeed): boolean =>
  isDisplayedAndChecked(
    need,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH,
  ) || isDisplayedAndChecked(need, ProductCoverageName.DEATH);

const collectiveDisabilityAdviceChecked = (need: CoverageNeed): boolean =>
  isDisplayedAndChecked(
    need,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK,
  ) ||
  isDisplayedAndChecked(
    need,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST,
  ) ||
  isDisplayedAndChecked(need, ProductCoverageName.DISABILITY_FLEX_PENSION) ||
  isDisplayedAndChecked(need, ProductCoverageName.DISABILITY_FLEX_CAPITAL);

const collectiveCriticalIllnessAdviceChecked = (need: CoverageNeed): boolean =>
  isDisplayedAndChecked(need, ProductCoverageName.AKA_CRITICAL_ILLNESS) ||
  isDisplayedAndChecked(need, ProductCoverageName.CRITICAL_ILLNESS);

const individualLifeAdviceChecked = (need: CoverageNeed): boolean =>
  isDisplayedAndChecked(need, ProductCoverageName.DEATH);

const individualDisabilityAdviceChecked = (need: CoverageNeed): boolean =>
  isDisplayedAndChecked(need, ProductCoverageName.DISABILITY_FLEX_PENSION) ||
  isDisplayedAndChecked(need, ProductCoverageName.DISABILITY_FLEX_CAPITAL);

const individualCriticalIllnessAdviceChecked = (need: CoverageNeed): boolean =>
  isDisplayedAndChecked(need, ProductCoverageName.CRITICAL_ILLNESS);

export const checkShouldPurchaseFromAdvisor = (
  isAdvisor: boolean,
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
): boolean =>
  checkShouldPurchasePaymentFromAdvisor(
    isAdvisor,
    getPayment(need, productCoverageName),
  );

export const checkShouldPurchasePaymentFromAdvisor = (
  isAdvisor: boolean,
  paymentConfiguration: PaymentConfiguration,
): boolean => {
  const { advice, restrictions } = paymentConfiguration;
  const { maxPayouts } = restrictions.paymentRestriction;
  return (
    !isAdvisor &&
    advice.recommended > maxPayouts[UserAuthorizationLevel.CUSTOMER]
  );
};

export const displayCanGetHigherFromAdvisor = (
  selectedAmount: number,
  need: CoverageNeed,
  productCoverageName: ProductCoverageName,
  isAdvisor: boolean,
): boolean => {
  const restrictions = getPaymentRestriction(need, productCoverageName);
  const maxCustomer = restrictions.maxPayouts[UserAuthorizationLevel.CUSTOMER];
  const maxAdvisor =
    restrictions.maxPayouts[UserAuthorizationLevel.PRODUCT_MANAGER];
  return (
    !isAdvisor &&
    !checkShouldPurchaseFromAdvisor(false, need, productCoverageName) &&
    selectedAmount === maxCustomer &&
    maxAdvisor - maxCustomer >= getMinContactAdvisorDiff(productCoverageName)
  );
};

const getMinContactAdvisorDiff = (
  productCoverageName: ProductCoverageName,
): number => {
  if (productCoverageName === ProductCoverageName.DISABILITY_FLEX_CAPITAL) {
    return CONTACT_ADVISOR_MIN_CAPITAL_DIFF;
  }
  return 1;
};

export const mapAdviceDataToPriceFormData = (
  adviceData: AkaAdvicePriceFormValues,
  products: InsuranceProduct[],
  advice: CollectiveAdvice,
  isAdvisor: boolean,
): AkaAdvicePriceFormValues => {
  const disabilityProduct = products.find(
    (product): product is DisabilityProduct =>
      product.name === INSURANCE_NAME_VALUE.DISABILITY_FLEX,
  );
  /**
   * These might not be included in the response, in which case they
   * need to explicitly be set to undefined to trigger form validation
   * correctly.
   */
  const {
    deathAdviceComment,
    disabilityAdviceComment,
    criticalIllnessAdviceComment,
  } = adviceData;
  return {
    ...adviceData,
    deathAdviceComment,
    disabilityAdviceComment,
    criticalIllnessAdviceComment,
    ...mapDisabilityCapitalData(
      adviceData,
      disabilityProduct,
      advice.needs[NeedName.DISABILITY],
      isAdvisor,
    ),
  };
};

type DisabilityCapitalData = Pick<
  AkaAdvicePriceFormValues,
  | "disabilityOneTimePaymentCheckbox"
  | "disabilityOneTimePaymentValue"
  | "disabilityOneTimePaymentAdvisorChoice"
  | "disabilityOneTimePaymentAdvisorValue"
  | "disabilityOneTimePaymentChangedWarning"
>;

const mapDisabilityCapitalData = (
  adviceData: AkaAdvicePriceFormValues,
  disabilityProduct: DisabilityProduct | undefined,
  need: CoverageNeed,
  isAdvisor: boolean,
): DisabilityCapitalData => {
  const {
    disabilityOneTimePaymentCheckbox,
    disabilityOneTimePaymentValue,
    disabilityOneTimePaymentAdvisorChoice,
    disabilityOneTimePaymentAdvisorValue,
  } = adviceData;
  const disabilityCapitalData = {
    disabilityOneTimePaymentCheckbox,
    disabilityOneTimePaymentValue,
    disabilityOneTimePaymentAdvisorChoice,
    disabilityOneTimePaymentAdvisorValue,
  };

  if (!isAdvisor) {
    return mapDisabilityCapitalCustomerData(
      disabilityCapitalData,
      disabilityProduct,
      need,
    );
  }
  return mapDisabilityCapitalAdvisorData(
    disabilityCapitalData,
    disabilityProduct,
    need,
  );
};

const mapDisabilityCapitalCustomerData = (
  disabilityCapitalData: DisabilityCapitalData,
  disabilityProduct: DisabilityProduct | undefined,
  need: CoverageNeed,
): DisabilityCapitalData => {
  const restrictions = getPaymentRestriction(
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );
  const minPayment = restrictions.minPayouts[UserAuthorizationLevel.CUSTOMER];
  const maxPayment = restrictions.maxPayouts[UserAuthorizationLevel.CUSTOMER];
  const hasSelectedDisabilityCapital =
    isDefined(disabilityProduct) &&
    isDefined(disabilityProduct.coverageAmountCapital) &&
    disabilityProduct.coverageAmountCapital > 0;
  if (
    hasSelectedDisabilityCapital &&
    (disabilityProduct.coverageAmountCapital === minPayment ||
      disabilityProduct.coverageAmountCapital === maxPayment)
  ) {
    return disabilityCapitalData;
  }
  return {
    ...disabilityCapitalData,
    disabilityOneTimePaymentCheckbox: false,
    disabilityOneTimePaymentValue: getCustomerInitialDisabilityCapital(need),
    disabilityOneTimePaymentChangedWarning: hasSelectedDisabilityCapital,
  };
};

const mapDisabilityCapitalAdvisorData = (
  disabilityCapitalData: DisabilityCapitalData,
  disabilityProduct: DisabilityProduct | undefined,
  need: CoverageNeed,
): DisabilityCapitalData => {
  const recommended = getRecommended(
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );
  const updatedData = {
    ...disabilityCapitalData,
    disabilityOneTimePaymentValue: recommended,
  };
  if (
    !isDefined(disabilityProduct) ||
    !isDefined(disabilityProduct.coverageAmountCapital) ||
    disabilityProduct.coverageAmountCapital === 0
  ) {
    return {
      ...updatedData,
      disabilityOneTimePaymentAdvisorChoice:
        OneTimePaymentAdvisorChoice.NO_ONE_TIME_PAYMENT,
    };
  }

  if (disabilityProduct.coverageAmountCapital === recommended) {
    return {
      ...updatedData,
      disabilityOneTimePaymentAdvisorChoice:
        OneTimePaymentAdvisorChoice.ADVICED_ONE_TIME_PAYMENT,
    };
  }

  return {
    ...updatedData,
    disabilityOneTimePaymentAdvisorChoice:
      OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT,
    disabilityOneTimePaymentAdvisorValue:
      disabilityProduct.coverageAmountCapital,
  };
};

const getCustomerInitialDisabilityCapital = (need: CoverageNeed): number => {
  const recommendedAmount = getRecommended(
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );
  if (
    isAllowedAmount(
      recommendedAmount,
      need,
      ProductCoverageName.DISABILITY_FLEX_CAPITAL,
      false,
    )
  ) {
    return recommendedAmount;
  }
  return 0;
};

export const isNeedSelectionEqualToAdvice = (
  needName: NeedName,
  formValues: Partial<AkaAdvicePriceFormValues>,
  advice: CollectiveAdvice,
  isAdvisor: boolean,
): boolean =>
  Object.values(advice.needs[needName].advice)
    .flatMap((insuranceAdvice) => Object.entries(insuranceAdvice.payments))
    .every(
      ([productCoverageName, payment]: [
        ProductCoverageName,
        PaymentConfiguration,
      ]) =>
        isPaymentSelectionEqualToAdvice(
          payment,
          productCoverageName,
          formValues,
          isAdvisor,
        ),
    );

const isPaymentSelectionEqualToAdvice = (
  payment: PaymentConfiguration,
  productCoverageName: ProductCoverageName,
  formValues: Partial<AkaAdvicePriceFormValues>,
  isAdvisor: boolean,
): boolean => {
  const paymentSelectionEqualsAdvice =
    getSelectedCoverageValue(productCoverageName, formValues, isAdvisor) ===
    payment.advice.recommended;

  if (productCoverageName === ProductCoverageName.DISABILITY_FLEX_PENSION) {
    return (
      paymentSelectionEqualsAdvice && !formValues.disabilityPaymentAfter3Months
    );
  }
  return paymentSelectionEqualsAdvice;
};

/**
 * Calculating vertical space used by comments in the individual advice PDF document.
 * Comments can use up to one page in the document.
 */
export const getPercentOfPDFDocumentCommentSpaceUsed = (
  formValues: Partial<AkaAdvicePriceFormValues>,
): number => {
  const comments = [
    formValues.deathAdviceComment,
    formValues.disabilityAdviceComment,
    formValues.criticalIllnessAdviceComment,
  ].filter(isNotEmpty);
  const paragraphs = comments
    .flatMap((comment) => comment.split("\n"))
    .filter(isNotEmpty);
  const lines = paragraphs.flatMap((paragraph) => paragraph.match(/.{1,104}/g));

  return Math.ceil(
    ((comments.length * 2 + paragraphs.length * 0.6 + lines.length) /
      DOCUMENT_MAX_COMMENT_LINES) *
      100,
  );
};
